import { useState } from 'react'
import {
  FileText,
  FolderPlus,
  FilePlus,
  Trash2,
  ChevronRight,
  ChevronDown,
  PanelLeftClose,
  PanelLeftOpen,
} from 'lucide-react'
import { useFileStore } from '../../stores/fileStore'
import { useEditorStore } from '../../stores/editorStore'
import { useUIStore } from '../../stores/uiStore'
import { useFileSystem } from '../../hooks/useFileSystem'
import type { FileItem } from '../../types'

export default function Sidebar() {
  const { files, activeFileId, setActiveFile, removeFile } = useFileStore()
  const { setContent } = useEditorStore()
  const { sidebarOpen, toggleSidebar, sidebarTab, setSidebarTab } = useUIStore()
  const { createFile, deleteFileById, importFile } = useFileSystem()

  const [expandedFolders, setExpandedFolders] = useState<Set<string>>(new Set())

  if (!sidebarOpen) {
    return (
      <button
        onClick={toggleSidebar}
        className="p-2 hover:bg-editor-hover text-editor-fg/70 hover:text-editor-fg transition-colors border-r border-editor-border"
        title="打开侧边栏"
      >
        <PanelLeftOpen size={18} />
      </button>
    )
  }

  const handleFileClick = (file: FileItem) => {
    if (file.isFolder) {
      setExpandedFolders((prev) => {
        const next = new Set(prev)
        if (next.has(file.id)) next.delete(file.id)
        else next.add(file.id)
        return next
      })
    } else {
      setActiveFile(file.id)
      setContent(file.content)
    }
  }

  const handleDeleteFile = (e: React.MouseEvent, id: string) => {
    e.stopPropagation()
    if (files.length <= 1) return
    deleteFileById(id)
  }

  const renderFile = (file: FileItem, depth: number = 0) => (
    <div key={file.id}>
      <div
        className={`flex items-center gap-1.5 px-2 py-1 cursor-pointer hover:bg-editor-hover text-sm transition-colors group ${
          file.id === activeFileId ? 'bg-editor-active text-editor-fg' : 'text-editor-fg/70'
        }`}
        style={{ paddingLeft: `${depth * 16 + 8}px` }}
        onClick={() => handleFileClick(file)}
      >
        {file.isFolder ? (
          expandedFolders.has(file.id) ? (
            <ChevronDown size={14} />
          ) : (
            <ChevronRight size={14} />
          )
        ) : (
          <FileText size={14} className="shrink-0" />
        )}
        <span className="truncate flex-1">{file.name}</span>
        {!file.isFolder && files.length > 1 && (
          <button
            onClick={(e) => handleDeleteFile(e, file.id)}
            className="opacity-0 group-hover:opacity-100 p-0.5 hover:text-red-500 transition-all"
            title="删除文件"
          >
            <Trash2 size={12} />
          </button>
        )}
      </div>
      {file.isFolder && expandedFolders.has(file.id) && file.children?.map((child) => renderFile(child, depth + 1))}
    </div>
  )

  return (
    <div className="flex flex-col h-full border-r border-editor-border bg-editor-sidebar shrink-0" style={{ width: 260 }}>
      <div className="flex items-center justify-between px-3 py-2 border-b border-editor-border">
        <span className="text-xs font-semibold uppercase tracking-wider text-editor-fg/50">资源管理器</span>
        <div className="flex items-center gap-1">
          <button
            onClick={() => createFile()}
            className="p-1 hover:bg-editor-hover rounded text-editor-fg/70 hover:text-editor-fg transition-colors"
            title="新建文件"
          >
            <FilePlus size={14} />
          </button>
          <button
            onClick={importFile}
            className="p-1 hover:bg-editor-hover rounded text-editor-fg/70 hover:text-editor-fg transition-colors"
            title="导入文件"
          >
            <FolderPlus size={14} />
          </button>
          <button
            onClick={toggleSidebar}
            className="p-1 hover:bg-editor-hover rounded text-editor-fg/70 hover:text-editor-fg transition-colors"
            title="关闭侧边栏"
          >
            <PanelLeftClose size={14} />
          </button>
        </div>
      </div>

      <div className="flex-1 overflow-auto py-1">
        {files.map((file) => renderFile(file))}
      </div>
    </div>
  )
}
